<template>
	<Html :class="[showMobileFilter ? 'overflow-hidden' : '']">
		<Body :class="[showMobileFilter ? 'overflow-hidden' : '']"></Body>
	</Html>

	<div class="bg-gray-50 pb-6 lg:py-8">
		<div class="container relative flex flex-wrap">
			<div
				class="sticky top-0 w-full lg:w-3/12"
				:class="[showMobileFilter ? 'z-[1000]' : 'z-20']"
			>
				<div class="flex h-20 items-center lg:hidden">
					<div
						class="mobile-filter-row flex w-full space-x-3 lg:hidden"
					>
						<div class="w-1/2">
							<IconButton
								:is-outline="false"
								:is-full-width="true"
								:background-color="'bg-blue-400'"
								@click="setMobileFilterDisplayState(true)"
							>
								<template #icon>
									<IconFilter class="h-4 w-4 text-white" />
								</template>
								Filter
							</IconButton>
						</div>
						<div class="flex w-1/2 justify-end">
							<ClientOnly>
								<DropdownMenu
									class="w-full"
									position="left"
									menu-button-class="w-full"
									:menu-items="sortByOptions"
									:show-caret="false"
									@click-menu-item="onSortByChange"
								>
									<template #menu-button>
										<IconButton
											:is-outline="false"
											:is-full-width="true"
											background-color="bg-blue-400"
										>
											<template #icon>
												<IconSortBy
													class="h-4 w-4 text-white"
												/>
											</template>
											Sort by
										</IconButton>
									</template>
								</DropdownMenu>
							</ClientOnly>
						</div>
					</div>
				</div>

				<FiltersAside :page-type="pageType" />
			</div>

			<div class="mt-8 w-full lg:mt-0 lg:w-9/12">
				<div class="flex flex-col border-gray-300 lg:border-b lg:pb-8">
					<BreadcrumbNavigation :crumbs="crumbs" />

					<h1 class="mb-0 mt-5">
						<template v-if="mounted">
							{{ pageMeta.onPageTitle }}
						</template>

						<template v-if="!mounted">
							<ContentLoader width="w-full" height="h-[50px]" />
						</template>
					</h1>
					<RemoveFilterCloud class="mt-5 hidden lg:flex" />
				</div>

				<div class="mt-4 flex flex-col lg:mt-6">
					<div class="flex justify-end">
						<div class="flex gap-x-6">
							<span
								v-if="
									pagination.total !== null &&
									pagination.total > 0 &&
									!isFiltering
								"
								class="text-base leading-normal text-gray-700"
							>
								{{ pagination.total }} cars
							</span>
							<ClientOnly>
								<DropdownMenu
									class="hidden lg:block"
									position="left"
									z-index="z-50"
									:menu-items="sortByOptions"
									:show-caret="false"
									@click-menu-item="onSortByChange"
								>
									<template #menu-title>
										<span
											class="leading-normal text-gray-700"
										>
											Sort by
										</span>
									</template>
									<template #menu-button>
										<span
											v-if="activeSortBy"
											class="cursor-pointer text-base text-gray-700 underline underline-offset-2 duration-300 ease-out hover:text-blue-400 hover:decoration-blue-400"
										>
											{{ activeSortBy.label }}
										</span>
									</template>
								</DropdownMenu>
							</ClientOnly>
						</div>
					</div>

					<div class="mt-4 lg:mt-6">
						<FilterResults
							:page-type="pageType"
							@update-page="updatePage"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeMount } from 'vue'
import { isEmpty as _isEmpty, find as _find, has as _has } from 'lodash-es'
import qs from 'qs'
import { navigateTo } from '#app'
import BreadcrumbNavigation from '@/components/breadcrumbs/BreadcrumbNavigation.vue'
import RemoveFilterCloud from '@/components/product-catalog/RemoveFilterCloud.vue'
import FiltersAside from '@/components/product-catalog/FiltersAside.vue'
import FilterResults from '@/components/product-catalog/FilterResults.vue'
import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue'
import {
	useProductCatalog,
	getDefaultFiltersState,
	getDefaultFacetState,
} from '@/stores/product-catalog'
import IconButton from '@/components/buttons/IconButton.vue'
import ContentLoader from '@/components/ContentLoader.vue'
import IconFilter from '@/assets/svg/product-catalog/ico-filter.svg'
import IconSortBy from '@/assets/svg/product-catalog/ico-svg-sortby.svg'

const productCatalog = useProductCatalog()

const props = defineProps({
	pageType: {
		type: String,
		default: 'subscribe',
	},
	pageBaseUrl: {
		type: String,
		default: '/cars/subscribe',
	},
	titleSlug: {
		type: String,
		default: 'Car Subscription',
	},
	gtmSearch: {
		type: String,
		default: 'vehicle_subscription_search',
	},
	gtmFilter: {
		type: String,
		default: 'subscription_search_filter',
	},
	response: {
		type: Object,
	},
})

const emits = defineEmits(['click'])

const router = useRouter()

const route = useRoute()

// TODO create helper function for breadcrumbs
const crumbs = computed(() => [
	{
		title: 'Home',
		url: '/',
	},
	{
		title: props.titleSlug,
	},
])

const showMobileFilter = computed(() => productCatalog.showMobileFilter)

const facets = computed(() => productCatalog.facets)

const filters = computed(() => productCatalog.filters)

const products = computed(() => productCatalog.products)

const sortByOptions = computed(() => productCatalog.facets.sortBy)

const activeSortBySlug = computed(() => productCatalog.filters.sortBy)

const activeSortBy = computed(() =>
	_find(sortByOptions.value, (item) => item.slug === activeSortBySlug.value)
)

const pagination = computed(() => productCatalog.productsPagination)

const pageMeta = computed(() =>
	productCatalog.getPageMetaFromFilters(props.titleSlug)
)

const isFiltering = computed(() => productCatalog.isFiltering)

const mounted = ref(false)

const silentRouterPush = ref(false)

const isSubscription = computed(() => props.pageType === 'subscribe')
const isBuyUsed = computed(() => props.pageType === 'used')

const gtmPrefix = computed(() =>
	isSubscription.value ? 'subscription_search' : 'buy_used'
)

/*******************
 *
 * HELPERS
 *
 *******************/
const setCanonicals = () => {
	let canonicalPath = route.path
	if (_has(route.query, 'page')) {
		canonicalPath = `${canonicalPath}?page=${route.query.page}`
	}

	updateOrCreateHeadLink('canonical', canonicalPath)
}

const setPrevNextLinks = () => {
	const prevPageUrl = productCatalog.getPrevFilterUrl

	if (prevPageUrl) {
		updateOrCreateHeadLink(
			'prev',
			`${prevPageUrl.path}${prevPageUrl.querystring}`
		)
	}

	const nextPageUrl = productCatalog.getNextFilterUrl
	if (nextPageUrl) {
		updateOrCreateHeadLink(
			'next',
			`${nextPageUrl.path}${nextPageUrl.querystring}`
		)
	}
}

const isNumeric = (value) => /^-?\d+$/.test(value)

/**
 * push current query to URL history
 */
const pushHistoryState = () => {
	if (typeof window === 'undefined') return

	silentRouterPush.value = true
	router.push(productCatalog.getFilterUrl)
	silentRouterPush.value = false
}

const initFiltersFromUrl = async (to) => {
	// we need to wait for the next tick to allow route.query to be populated with the updated querystring
	// if we don't have this, the querystring reflects the "old" state and filtering/pagination does not work.
	await nextTick()

	const urlFilters = getDefaultFiltersState()

	const finder = (param, facetsToSearch) =>
		!_isEmpty(
			_find(facetsToSearch, (item) =>
				param.includes(item.value.toLowerCase())
			)
		)

	const decodeParam = (param) => decodeURI(param).toLowerCase().split(',')

	const initRoute = to !== undefined ? to : route

	const slugParams = ['fueltypes', 'bodytypes', 'states', 'makes', 'models']

	const defaultFacets = getDefaultFacetState()

	if (initRoute.params.slug) {
		initRoute.params.slug.forEach((param) => {
			const decodedParam = decodeParam(param)

			if (finder(decodedParam, defaultFacets.fuelTypes)) {
				urlFilters.fuelTypes = decodedParam
			} else if (finder(decodedParam, defaultFacets.bodyTypes)) {
				urlFilters.bodyTypes = decodedParam
			} else if (finder(decodedParam, defaultFacets.states)) {
				urlFilters.states = decodedParam
			} else if (!(urlFilters.makes && urlFilters.makes.length)) {
				urlFilters.makes = decodedParam
			} else {
				urlFilters.models = decodedParam
			}
		})
	}

	const queryParams = qs.parse(initRoute.query)

	const queryFilters = queryParams

	for (const [key, item] of Object.entries(queryFilters)) {
		if (facets.value[key] && !slugParams.includes(key)) {
			let values = item
			if (Array.isArray(filters.value[key])) {
				values = `${item}`.split(',')
				values = values.map((value) =>
					isNumeric(value) ? parseInt(value, 10) : value
				)
			}

			urlFilters[key] = values
		}
	}

	// handle sort-by parameters
	if (queryParams.sort) {
		urlFilters.sortBy = queryParams.sort
	}

	await productCatalog.silentFilter(async () => {
		await productCatalog.updateFilters({
			filters: urlFilters,
			overwrite: true,
			doNotSetIsDirty: true,
		})

		// handle pagination parameter
		await productCatalog.updateProductsPagination({
			filterNextPage: queryParams?.page
				? parseInt(queryParams.page, 10)
				: 1,
		})
	})
}

const setMobileFilterDisplayState = (state) => {
	productCatalog.showMobileFilter = state
	window.zE?.hide()
}

const gtmTrack = (event) => {
	if (isSubscription.value) {
		trackGtmEvent({
			event,
			product_category: 'Subscription',
			make: productCatalog.getLabelByActiveFilterKey('makes'),
			model: productCatalog.getLabelByActiveFilterKey('models'),
			body_type: productCatalog.getLabelByActiveFilterKey('bodyTypes'),
			location: productCatalog.getLabelByActiveFilterKey(
				'states',
				false,
				true
			),
			new_or_used: productCatalog.getLabelByActiveFilterKey('conditions'),
			subscription_price_max:
				productCatalog.getLabelByActiveFilterKey('priceTo'),
			subscription_price_min:
				productCatalog.getLabelByActiveFilterKey('priceFrom'),
			year_max: productCatalog.getLabelByActiveFilterKey('yearTo'),
			year_min: productCatalog.getLabelByActiveFilterKey('yearFrom'),
			transmission:
				productCatalog.getLabelByActiveFilterKey('transmissions'),
			fuel_type: productCatalog.getLabelByActiveFilterKey('fuelTypes'),
			seat_capacity: productCatalog.getLabelByActiveFilterKey('seats'),
			drive_type: productCatalog.getLabelByActiveFilterKey('driveTypes'),
			doors: productCatalog.getLabelByActiveFilterKey('doors'),
			features: productCatalog.getLabelByActiveFilterKey('features'),
			sort_by: productCatalog.getLabelByActiveFilterKey('sortBy'),
			page: productCatalog.productsPagination.currentPage,
			number_of_listings: productCatalog.productsPagination.total,
		})
	} else if (isBuyUsed.value) {
		trackGtmEvent({
			event,
			product_category: 'Buy Used',
			make: productCatalog.getLabelByActiveFilterKey('makes'),
			model: productCatalog.getLabelByActiveFilterKey('models'),
			body_type: productCatalog.getLabelByActiveFilterKey('bodyTypes'),
			location: productCatalog.getLabelByActiveFilterKey(
				'states',
				false,
				true
			),
			purchase_price_max:
				productCatalog.getLabelByActiveFilterKey('priceUsedTo'),
			purchase_price_min:
				productCatalog.getLabelByActiveFilterKey('priceUsedFrom'),
			year_max: productCatalog.getLabelByActiveFilterKey('yearTo'),
			year_min: productCatalog.getLabelByActiveFilterKey('yearFrom'),
			transmission:
				productCatalog.getLabelByActiveFilterKey('transmissions'),
			fuel_type: productCatalog.getLabelByActiveFilterKey('fuelTypes'),
			drive_type: productCatalog.getLabelByActiveFilterKey('driveTypes'),
			odometer_min:
				productCatalog.getLabelByActiveFilterKey('odometerFrom'),
			odometer_max:
				productCatalog.getLabelByActiveFilterKey('odometerTo'),
			sort_by: productCatalog.getLabelByActiveFilterKey('sortBy'),
			page: productCatalog.productsPagination.currentPage,
			number_of_listings: productCatalog.productsPagination.total,
		})
	}
}

/*******************
 *
 * PAGE LOAD
 *
 * *****************/

const loadStore = async () => {
	await productCatalog.fetchPage(props.pageType)
}

onBeforeMount(() => loadStore())

onMounted(async () => {
	setCanonicals()
	await initFiltersFromUrl()

	// handle if someone has loaded up the URL in the incorrect order (e.g. /audi/qld instead of /qld/audi)
	// we ignore the querystring as we want to leave support in for GTM tracking links.
	if (decodeURI(productCatalog.getFilterUrl.path) !== decodeURI(route.path)) {
		const expectedUrl = productCatalog.getFilterUrl

		return navigateTo(`${expectedUrl.path}${expectedUrl.querystring}`, {
			redirectCode: 301,
			replace: true,
		})
	}

	// we want to set the isDirty state AFTER we have completed any redirects.
	productCatalog.setIsDirtyState(true)

	// handle when the server side initial filter fails
	// by initial filtering on the client side
	if (products.value.length === 0) {
		await productCatalog.setFilteringState(true)

		filter()

		gtmTrack(`${gtmPrefix.value}_filter`)
	}

	mounted.value = true

	// satisfy eslint return requirement
	return true
})

/*******************
 *
 * EVENTS / FILTERING
 *
 * *****************/

// filters are updated by ProductCatalog, but it doesn't trigger anything. We watch for changes here
watch(
	filters,
	async (newValue, oldValue) => {
		if (!mounted.value) return

		if (productCatalog.isSilentFilter || productCatalog.isFiltering) {
			return
		}

		await productCatalog.updateProductsPagination({
			filterNextPage: 1,
		})

		await onFiltersChange(
			newValue.sortBy === oldValue.sortBy
				? `${gtmPrefix.value}_filter`
				: `${gtmPrefix.value}_sort`
		)
	},
	{
		deep: true,
	}
)

// triggered by FilterResults Pagination
const updatePage = async (page) => {
	if (!mounted.value) return
	if (productCatalog.isSilentFilter) return

	await productCatalog.updateProductsPagination({
		filterNextPage: page,
	})

	await onFiltersChange(`${gtmPrefix.value}_filter`)
}

// this is used for the forward/back navigation via the browser. There are a number of issues to be aware of:
//
// 1. afterEach is called multiple times on each route change
// 2. the filter routine pushes a new url, which triggers this hook
// 3. the route.fullPAth is often outdated compared to from/to
//
// we try to avoid this hook causing problems by:
//
// * don't run if the page isn't mounted
// * the catalog is filtering, or is in silent mode
// * a variable set in pushHistory is set to true
// * the from/to are the same, or, the to matches the route.fullPath
// * if we are to run this hook, we set filtering state to avoid anything else running
router.afterEach(async (to, from) => {
	// don't run if we aren't mounted, we are filtering, or are pushing route history, or the URL's match
	const decodedTo = decodeURI(to.fullPath)
	if (
		!mounted.value ||
		productCatalog.isSilentFilter ||
		silentRouterPush.value ||
		productCatalog.isFiltering ||
		decodedTo === decodeURI(from.fullPath) ||
		decodedTo === decodeURI(route.fullPath)
	) {
		return false
	}
	await productCatalog.setFilteringState(true)

	await initFiltersFromUrl(to)

	setCanonicals()
	setPrevNextLinks()

	await productCatalog.silentFilter(() => {
		productCatalog.setFilteringState(true)

		filter(
			to.query.sort === from.query.sort
				? `${gtmPrefix.value}_filter`
				: `${gtmPrefix.value}_sort`
		)
	})

	return true
})

const onFiltersChange = (gtmOption) => {
	productCatalog.setFilteringState(true)
	filter()
	gtmTrack(gtmOption)
	pushHistoryState()
}

const onSortByChange = (menuItem) => {
	if (menuItem.slug === filters.value.sortBy) {
		return
	}

	productCatalog.updateFilters({
		filters: {
			sortBy: menuItem.slug,
		},
	})
}

const filter = () => {
	productCatalog.filter({})
	setCanonicals()
	setPrevNextLinks()
}

watchEffect(() => {
	if (!pageMeta.value) return

	useHead({
		title: pageMeta.value?.title,
		meta: [
			{
				name: 'description',
				content: pageMeta.value?.description,
			},
		],
	})

	useSeoMeta({
		ogTitle: pageMeta.value.title,
		ogDescription: pageMeta.value.description,
		twitterTitle: pageMeta.value.title,
		twitterDescription: pageMeta.value.description,
	})
})
</script>
